import React, { useState, useEffect } from 'react'
import validate from 'validate.js'
import clsx from 'clsx'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/styles'
import { TextField, Button, InputAdornment, IconButton } from '@material-ui/core'
import {
  Visibility,
  VisibilityOff,
  Lock,
  Person
} from '@material-ui/icons'

import { useSessionService } from 'hooks/useSessionService'
import useRouter from 'utils/useRouter'
import { Alert } from 'components'

const schema = {
  cpf: {
    format: {
      pattern: '([0-9]{2}[.]?[0-9]{3}[.]?[0-9]{3}[/]?[0-9]{4}[-]?[0-9]{2})|([0-9]{3}[.]?[0-9]{3}[.]?[0-9]{3}[-]?[0-9]{2})',
      message: '^Digite um CPF válido',
    },
    presence: { allowEmpty: false, message: '^CPF é obrigatório' },
  },
  senha: {
    presence: { allowEmpty: false, message: '^Senha é obrigatório' },
    length: {
      minimum: 8,
      message: '^No mínimo 8 caracteres',
    },
  },
  senhaVerificar: {
    presence: {
      allowEmpty: false,
      message: '^Repetir senha é obrigatório',
    },
    equality: {
      attribute: 'senha',
      message: '^As senhas devem ser iguais',
    },
  },
}

const useStyles = makeStyles((theme) => ({
  root: {},

  fields: {
    margin: theme.spacing(-1),
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      flexGrow: 1,
      margin: theme.spacing(1),
    },
  },

  alert: {
    marginBottom: theme.spacing(2),
  },

  submitButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    padding: '12px 24px !important',
    fontSize: '0.95rem !important',
    fontWeight: '600 !important',
    borderRadius: '8px !important',
    boxShadow: 'none !important',
    backgroundColor: '#0A2540 !important', // Sempre Midnight Navy
    color: '#FFFFFF !important', // Sempre texto branco
    transition: 'all 0.2s ease-in-out !important',
    '&:hover': {
      backgroundColor: '#051221 !important', // Sempre Midnight Navy Escuro
      boxShadow: '0 4px 12px rgba(10, 37, 64, 0.2) !important',
      transform: 'translateY(-1px) !important',
    },
    '&:active': {
      transform: 'translateY(0) !important',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
      color: 'rgba(0, 0, 0, 0.26) !important',
      boxShadow: 'none !important',
      transform: 'none !important',
    }
  },

  iconAdornment: {
    color: '#64748B', // Ícones decorativos cinza fixo
    marginRight: theme.spacing(1),
  },

  passwordToggle: {
    padding: 8,
    color: '#64748B', // Olho de visualização de senha cinza fixo
  }

}))

export const Formulario = (props) => {
  const { className, token, ...rest } = props
  const classes = useStyles()
  const router = useRouter()
  const sessionService = useSessionService()
  const usuarios = useSelector((state) => state.usuarios)
  const [showPassword, setShowPassword] = useState(false)
  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
    cpfValido: false,
  })

  useEffect(() => {
    const errors = validate(formState.values, schema)

    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }))
  }, [formState.values])

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev)
  }


  /**
   * @description Formata o Input CPF
   *
   * @param {*} value valor do campo CPF
   */
  const cpfMask = (value) =>
    value
      .replace(/\D/g, '') // substitui qualquer caracter que nao seja numero por nada
      .replace(/(\d{3})(\d)/, '$1.$2') // captura 2 grupos de numero o primeiro de 3 e o segundo de 1, apos capturar o primeiro grupo ele adiciona um ponto antes do segundo grupo de numero
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1') // captura 2 numeros seguidos de um traço e não deixa ser digitado mais nada

  /**
   * @description Valida o número de CPF
   *
   * @param {String} number String com o número do CPF
   * @returns {Boolean} Caso o CPF seja um número válido
   */
  const validateCPF = (number) => {
    const cpf = number.replace(/[/.-]/g, '') //! Limpar os possiveis caracteres ".", "-" e " " que venham junto aos números do CPF

    let Soma = 0
    let Resto

    for (let i = 1; i <= 9; i += 1) {
      Soma += parseInt(cpf.substring(i - 1, i), 10) * (11 - i)
    }

    Resto = (Soma * 10) % 11

    if (Resto === 10 || Resto === 11) {
      Resto = 0
    }

    if (Resto !== parseInt(cpf.substring(9, 10), 10)) {
      return false
    }

    Soma = 0

    for (let j = 1; j <= 10; j += 1) {
      Soma += parseInt(cpf.substring(j - 1, j), 10) * (12 - j)
    }

    Resto = (Soma * 10) % 11

    if (Resto === 10 || Resto === 11) {
      Resto = 0
    }

    if (Resto !== parseInt(cpf.substring(10, 11), 10)) {
      return false
    }

    return true
  }

  const handleChange = (event) => {
    event.persist()
    if (event.target.name === 'cpf') {
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : cpfMask(event.target.value),
        },
        touched: {
          ...formState.touched,
          [event.target.name]: true,
        },
      }))
    } else {
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
        },
        touched: {
          ...formState.touched,
          [event.target.name]: true,
        },
      }))
    }
    /**
     * @description Caso o input modificado seja o do CPF verifique sua validade
     */
    if (event.target.name === 'cpf') {
      setFormState((formState) => ({
        ...formState,
        cpfValido: validateCPF(event.target.value),
      }))
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    await sessionService.redefinirSenha(formState.values, token)
    router.history.push('/auth/login')
    setFormState({
      isValid: false,
      values: {},
      touched: {},
      errors: {},
      cpfValido: false,
    })
  }

  const hasError = (field) => formState.touched[field] && formState.errors[field]

  return (
    <form {...rest} className={clsx(classes.root, className)} onSubmit={handleSubmit}>
      <div className={classes.alert}>
        {usuarios.erroToken ? <Alert variant="error" message="Token de redefinição inválido" /> : null}
        {usuarios.redefinir_senha_sucesso ? <Alert variant="success" message="Senha redefinida com sucesso" /> : null}
      </div>

      <div className={classes.fields}>
        <TextField
          error={hasError('cpf') || (formState.touched.cpf && !formState.cpfValido)}
          fullWidth
          helperText={hasError('cpf') || (formState.touched.cpf && !formState.cpfValido) ? 'Digite um CPF válido' : null}
          label="CPF"
          name="cpf"
          onChange={handleChange}
          value={formState.values.cpf || ''}
          variant="outlined"
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" className={classes.iconAdornment}>
                <Person fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div className={classes.fields}>
        <TextField 
          error={hasError('senha')} 
          fullWidth 
          helperText={hasError('senha') ? formState.errors.senha[0] : null} 
          label="Senha" 
          name="senha" 
          onChange={handleChange} 
          type={showPassword ? 'text' : 'password'} 
          value={formState.values.senha || ''} 
          variant="outlined" 
          required 
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" className={classes.iconAdornment}>
                <Lock fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Alternar visibilidade da senha"
                  onClick={toggleShowPassword}
                  edge="end"
                  className={classes.passwordToggle}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          
        />

        <TextField
          error={hasError('senhaVerificar')}
          fullWidth
          helperText={hasError('senhaVerificar') ? formState.errors.senhaVerificar[0] : null}
          label="Repetir senha"
          name="senhaVerificar"
          onChange={handleChange}
          type={showPassword ? 'text' : 'password'}
          value={formState.values.senhaVerificar || ''}
          variant="outlined"
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" className={classes.iconAdornment}>
                <Lock fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Alternar visibilidade da senha"
                  onClick={toggleShowPassword}
                  edge="end"
                  className={classes.passwordToggle}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

      </div>
      <Button className={classes.submitButton} color="secondary" disabled={!formState.isValid} size="large" type="submit" variant="contained">
        Redefinir
      </Button>
    </form>
  )
}
